import React, { useCallback, useEffect, useRef, useState } from 'react';
import * as modelService from '../../services/modelService';
import type { PickerModel, PickerResponse } from './modelPickerTypes';
import ModelPickerSheet from './ModelPickerSheet';
import { usePopoverDisclosure } from '../../hooks/usePopoverDisclosure';

// NOTE: ModelPickerSheet is imported EAGERLY (not React.lazy) — its import graph
// is api-free, and React.lazy + Suspense does not resolve in CRA-jest/jsdom (the
// sheet test would hang on the fallback). Eager keeps the sheet testable for real.

export interface ModelPickerProps {
  entityId: string;
  value?: string | null; // current active slug (parent-controlled)
  onModelSelect: (slug: string) => void;
  className?: string;
}

const CHIP_CAP = 5;

export const ModelPicker: React.FC<ModelPickerProps> = ({ entityId, value, onModelSelect, className = '' }) => {
  const [data, setData] = useState<PickerResponse | null>(null);
  const moreRef = useRef<HTMLButtonElement>(null);
  // Disclosure owns Esc + focus-return-to-moreRef; the sheet's backdrop owns outside-click,
  // so closeOnOutsideClick:false (and panelRef is unneeded).
  const { isOpen: sheetOpen, setOpen: setSheetOpen } = usePopoverDisclosure({
    getTriggerEl: () => moreRef.current,
    closeOnOutsideClick: false,
  });
  const openSheet = useCallback(() => setSheetOpen(true), [setSheetOpen]);

  const load = useCallback(async () => {
    try { setData(await modelService.getPicker(entityId)); } catch { /* keep prior */ }
  }, [entityId]);
  useEffect(() => { load(); }, [load]);

  const active = value ?? data?.active ?? null;
  const favs: PickerModel[] = [...(data?.favorites.user ?? []), ...(data?.favorites.system ?? [])];
  const seen = new Set<string>();
  const chips: { slug: string; name: string }[] = [];
  if (active) { chips.push({ slug: active, name: shortName(active, data) }); seen.add(active); }
  for (const m of favs) {
    if (chips.length >= CHIP_CAP) break;
    if (seen.has(m.slug)) continue;
    seen.add(m.slug);
    chips.push({ slug: m.slug, name: m.name });
  }

  const pick = (slug: string) => {
    onModelSelect(slug);
    setSheetOpen(false);
    // record per-user recents (fire-and-forget; the parent records the session default)
    modelService.recordRecent(slug).catch(() => {});
  };

  return (
    <div className={`flex items-center gap-1 ${className}`} data-testid="model-picker">
      {chips.map((c) => (
        <button
          key={c.slug}
          type="button"
          onClick={() => pick(c.slug)}
          className={[
            'px-2 py-1 rounded-md text-xs whitespace-nowrap border transition-colors',
            c.slug === active
              ? 'border-emerald-500 bg-emerald-50 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100 dark:border-emerald-500/70'
              : 'border-gray-300 text-gray-700 hover:border-emerald-400 dark:border-gray-600 dark:text-gray-200 dark:hover:border-emerald-400',
          ].join(' ')}
        >
          {c.slug === active ? '● ' : ''}{c.name}
        </button>
      ))}
      <button
        ref={moreRef}
        type="button"
        data-testid="model-picker-more"
        onClick={openSheet}
        className="px-2 py-1 rounded-md text-xs border border-dashed border-gray-300 text-gray-500 hover:border-emerald-400 dark:border-gray-600 dark:text-gray-400"
      >
        + more…
      </button>
      {sheetOpen && data && (
        <ModelPickerSheet
          data={data}
          active={active}
          getAnchorEl={() => moreRef.current}
          onPick={pick}
          onToggleFavorite={async (slug, fav) => {
            if (fav) await modelService.addFavorite(slug);
            else await modelService.removeFavorite(slug);
            await load();
          }}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </div>
  );
};

function shortName(slug: string, data: PickerResponse | null): string {
  const all = [
    ...(data?.favorites.user ?? []), ...(data?.favorites.system ?? []),
    ...(data?.recents ?? []), ...(data?.new ?? []),
    ...((data?.providers ?? []).flatMap((p) => p.models)),
  ];
  return all.find((m) => m.slug === slug)?.name ?? slug.split('/').pop() ?? slug;
}

export default ModelPicker;
